import { makeStyles } from '@material-ui/core'
import { unwrapResult } from '@reduxjs/toolkit'
import { merge, pickBy } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import * as Yup from 'yup'

import { useAuth } from 'src/modules/auth'
import { useActionRunner } from 'src/modules/notification'
import {
  Button,
  Container,
  FormikTabForm,
  PageTitle,
  getFileParam,
  FIELD_REQUIRED,
} from 'src/modules/ui'
import AwardsForm, {
  validationSchema as awardsValidationSchema,
} from 'src/modules/ui/components/AwardsForm'
import SustainabilityForm, {
  initialValues as sustainbilityInitialValues,
  parseErrorsResponse as parseSustainabilityErrorsResponse,
  parseValuesForRequest as parseSustainabilityValuesForRequest,
  validationSchema as sustainabilityValidationSchema,
} from 'src/modules/ui/components/SustainabilityForm'

import { useProducts } from '../../../products/hooks'
import { useAdmin } from '../../hooks'
import BusinessDetails from './BusinessDetails'
import ProfileInfo from './ProfileInfo'

const useStyles = makeStyles(theme => ({
  header: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('mobile')]: {
      alignItems: 'flex-start',
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 0,
    },
  },
  viewProfile: {
    marginTop: theme.spacing(-2),
    [theme.breakpoints.up('mobile')]: {
      marginTop: 0,
    },
  },
}))

export const SupplierProfile = ({
  onSubmit,
  sustainability,
  sustainabilityOptions,
  ...supplierProfile
}) => {
  const classes = useStyles()
  const routeBase = '/admin/supplier-profile'
  const tabs = [
    {
      label: 'Business Details',
      path: `${routeBase}/business-details`,
      component: BusinessDetails,
      formRemainingFields: true,
    },
    {
      label: 'Profile Info',
      path: `${routeBase}/profile-info`,
      component: ProfileInfo,
      formFields: ['logo', 'coverImage', 'description', 'videoUrl', 'photos'],
    },
    {
      label: 'Sustainability',
      path: `${routeBase}/sustainability`,
      render: () => (
        <SustainabilityForm sustainabilityOptions={sustainabilityOptions} />
      ),
      formFields: ['sustainability'],
    },
    {
      label: 'Awards',
      path: `${routeBase}/awards`,
      component: AwardsForm,
      formFields: ['awards'],
    },
  ]

  const handleSubmit = async (values, { setErrors }) => {
    const {
      awards,
      coverImage,
      defaultDeliveryTime,
      defaultMinimumOrder,
      defaultOrderCutoff,
      defaultPaymentTerms,
      logo,
      photos,
      sustainability,
      ...valuesRest
    } = values

    const sustainabilityItems = await parseSustainabilityValuesForRequest(
      sustainability,
      sustainabilityOptions
    )

    const params = {
      ...valuesRest,
      awards,
      coverImageData: await getFileParam(coverImage),
      defaultDeliveryTime: defaultDeliveryTime || null,
      defaultMinimumOrder: defaultMinimumOrder || null,
      defaultPaymentTerms: defaultPaymentTerms || null,
      defaultOrderCutoff: defaultOrderCutoff
        ? defaultOrderCutoff.format('HH:mm')
        : null,
      logoData: await getFileParam(logo),
      photoItems: await Promise.all(
        photos.map(async ({ photo, ...rest }) => ({
          ...rest,
          photoData: await getFileParam(photo),
        }))
      ),
      sustainabilityItems,
    }

    const result = await onSubmit(params)
    try {
      unwrapResult(result)
    } catch (err) {
      if (err.data) {
        const {
          coverImageData,
          logoData,
          sustainabilityItems,
          ...fieldErrors
        } = err.data
        fieldErrors.coverImage = coverImageData
        fieldErrors.logoData = logoData

        if (sustainabilityItems) {
          fieldErrors.sustainability = parseSustainabilityErrorsResponse(
            sustainabilityItems,
            params.sustainabilityItems
          )
        }
        setErrors(fieldErrors)
      }
    }
  }

  const {
    awards,
    defaultOrderCutoff,
    registeredAddress,
    ...supplierProfileRest
  } = supplierProfile
  const initialValues = {
    name: '',
    phoneNumber: '',
    primaryContactName: '',
    primaryContactEmail: '',
    registeredAddress: {
      name: '',
      line1: '',
      line2: '',
      city: '',
      county: '',
      postcode: '',
    },
    deliveryArea: '',
    orderNotificationEmail: '',
    defaultDeliveryTime: '',
    defaultOrderCutoff: defaultOrderCutoff
      ? moment(defaultOrderCutoff, 'HH:mm')
      : null,
    defaultMinimumOrder: '',
    defaultPaymentTerms: '',
    awards: awards.map(({ supplier, ...award }) => ({ ...award })),
    photos: [],
    sustainability: sustainbilityInitialValues(
      sustainability,
      sustainabilityOptions
    ),
  }
  merge(
    initialValues,
    { registeredAddress: pickBy(registeredAddress) },
    pickBy(supplierProfileRest)
  )

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(FIELD_REQUIRED),
    phoneNumber: Yup.string().required(FIELD_REQUIRED),
    primaryContactName: Yup.string().required(FIELD_REQUIRED),
    primaryContactEmail: Yup.string().required(FIELD_REQUIRED),
    registeredAddress: Yup.object().shape({
      name: Yup.string().required(FIELD_REQUIRED),
      line1: Yup.string().required(FIELD_REQUIRED),
      line2: Yup.string(),
      city: Yup.string().required(FIELD_REQUIRED),
      county: Yup.string().required(FIELD_REQUIRED),
      postcode: Yup.string().required(FIELD_REQUIRED),
    }),
    deliveryArea: Yup.string(),
    orderNotificationEmail: Yup.string(),
    defaultDeliveryTime: Yup.number().typeError('Invalid number'),
    defaultOrderCutoff: Yup.date().typeError('Invalid time').nullable(),
    defaultMinimumOrder: Yup.number().typeError('Invalid number'),
    defaultPaymentTerms: Yup.number().typeError('Invalid number'),
    photos: Yup.array().of(
      Yup.object().shape({
        photo: Yup.string().required(FIELD_REQUIRED),
      })
    ),
    sustainability: sustainabilityValidationSchema(sustainabilityOptions),
    awards: awardsValidationSchema(),
  })
  return (
    <Container>
      <div className={classes.header}>
        <PageTitle>Supplier Profile</PageTitle>
        <Button
          className={classes.viewProfile}
          color="lightGreen"
          component="a"
          href={`/public/suppliers/${supplierProfile.id}`}
          target="_blank"
        >
          View Public Profile
        </Button>
      </div>
      <FormikTabForm
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        tabs={tabs}
        validationSchema={validationSchema}
      />
    </Container>
  )
}

SupplierProfile.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string,
  phoneNumber: PropTypes.string,
  primaryContactName: PropTypes.string,
  primaryContactEmail: PropTypes.string,
  registeredAddress: PropTypes.shape({
    name: PropTypes.string,
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    county: PropTypes.string,
    postcode: PropTypes.string,
  }),
  deliveryArea: PropTypes.string,
  orderNotificationEmail: PropTypes.string,
  defaultDeliveryTime: PropTypes.number,
  defaultOrderCutoff: PropTypes.string,
  defaultMinimumOrder: PropTypes.string,
  defaultPaymentTerms: PropTypes.number,
  awards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      year: PropTypes.number,
    })
  ),
  sustainability: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      expiry: PropTypes.string,
      document: PropTypes.string,
    })
  ),
  onSubmit: PropTypes.func,
  sustainabilityOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      image: PropTypes.string,
      name: PropTypes.string,
      group: PropTypes.string,
      question: PropTypes.string,
      appliesToSupplier: PropTypes.bool,
      appliesToProduct: PropTypes.bool,
      appliesToProductCategory: PropTypes.array,
    })
  ),
}

const SupplierProfileContainer = () => {
  const { user } = useAuth()
  const { supplierOptions } = useProducts()
  const { fetchProfile, profile, updateProfile } = useAdmin()
  const [doFetchProfile, isFetching] = useActionRunner(fetchProfile, {
    initialFetchingState: true,
  })
  const [doUpdateProfile] = useActionRunner(updateProfile, {
    successNotification: 'Profile updated!',
    errorNotification: result => {
      try {
        unwrapResult(result)
      } catch (err) {
        if (err.status === 400) {
          return 'Please correct all errors on the form'
        }
      }
    },
  })

  useEffect(() => {
    doFetchProfile()
  }, [])

  if (isFetching) {
    return null
  }

  const { sustainability } = supplierOptions

  return (
    <SupplierProfile
      {...profile}
      id={user.organisation.id}
      onSubmit={doUpdateProfile}
      sustainabilityOptions={sustainability}
    />
  )
}

export default SupplierProfileContainer
